import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import {
  AppDatePicker,
  AppFileUploader,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import Translator from '../../../../../components/helpers/Translator';
import { axiosPath } from '../../../../../../routing/helpers/router';
import minions from '../../../../../../minions/minions';
import errorHandler from '../../../../../../layout/components/error/ErrorHandler';
import AppFileUploaderList from '../../../../../components/form/File/AppFileUploaderList';
import useIsMounted from '../../../../../components/hooks/useIsMounted';
import { handleDisabled, handleMinDate } from '../../tools/comsaReportsTools';

export default function ModuleComsaFoodWasteUpdate(props) {
  const { report, OnRechargeReports, moduleData } = props;
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const [showModalReport, setShowModalReport] = useState(false);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator
        file='user'
        name={`calendar.month${i + 1}`}
      />,
      value: i + 1,
    }))
  );
  const validationSchema = Yup.object().shape({
    year: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    month: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    kilosFoodWaste: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    kilosFoodDonated: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    companyFoodWastePreventionPlan: Yup.string()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    companyAgreementsThirdSectorPreventive: Yup.string()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
  });

  const handleUpdateReport = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath('api_r_user_module_comsaReports_editReport'),
          minions.forms.toFormData({
            ...payload,
            id: report.id,
            type: 'foodWaste',
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        .then(() => {
          if (isMounted.current) OnRechargeReports();
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setShowModalReport(false)),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  return (
    <>
      <button
        type="button"
        className="btn btn-info btn-xs mr-1"
        onClick={() => setShowModalReport(true)}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit-food-waste" />,
        )}
        disabled={handleDisabled(moduleData, report?.year)}
      >
        <AppRemixIcon icon="edit" />
      </button>

      <AppModal
        show={showModalReport}
        title={ReactDOMServer.renderToString(
          <Translator file="module-comsaReports" name="edit-food-waste" />,
        )}
        size="xl"
        onClose={() => setShowModalReport(false)}
      >
        <Formik
          initialValues={{
            month: parseInt(report.month),
            year: report.year,
            kilosFoodWaste: report.kilosFoodWaste,
            kilosFoodDonated: report.kilosFoodDonated,
            companyFoodWastePreventionPlan: report.companyFoodWastePreventionPlan,
            companyAgreementsThirdSectorPreventive:
              report.companyAgreementsThirdSectorPreventive,
            files: [],
            deletedFiles: [],
            comment: report.comment,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleUpdateReport}
        >
          {({ setFieldValue, handleSubmit, values, errors }) => (
            <>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <AppDatePicker
                      title={<Translator file="module-comsaReports" name="year" />}
                      value={values.year}
                      onChange={(opt) => setFieldValue('year', opt)}
                      minDate={handleMinDate(moduleData)}
                      dateFormat="yyyy"
                      displayDateFormat="YYYY"
                      placeholder={
                        <Translator file="module-comsaReports" name="select.a.year" />
                      }
                      isClearable
                      showYearPicker
                      popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      error={errors.year}
                    />

                    <AppInputNumber
                      icon={<AppRemixIcon icon="asterisk" noVariants />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="kilos-food-waste"
                        />,
                      )}
                      value={values.kilosFoodWaste}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="value-kilos" />,
                      )}
                      onChange={(v) => setFieldValue('kilosFoodWaste', v)}
                      isClearable
                      error={errors.kilosFoodWaste}
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <AppSingleSelect
                      options={months}
                      onChange={(opt) => setFieldValue('month', opt)}
                      title={<Translator file="module-comsaReports" name="month" />}
                      icon={<AppRemixIcon icon="calendar" />}
                      placeholder={
                        <Translator
                          file="module-comsaReports"
                          name="select.a.month"
                        />
                      }
                      value={values.month}
                      error={errors.month}
                    />
                    <AppInputNumber
                      icon={<AppRemixIcon icon="truck" />}
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-comsaReports"
                          name="kilos-food-donated"
                        />,
                      )}
                      value={values.kilosFoodDonated}
                      placeholder={ReactDOMServer.renderToString(
                        <Translator file="module-comsaReports" name="value-kilos" />,
                      )}
                      onChange={(v) => setFieldValue('kilosFoodDonated', v)}
                      isClearable
                      error={errors.kilosFoodDonated}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <AppTextArea
                    icon={<AppRemixIcon icon="font-color" noVariants />}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaReports"
                        name="company-food-waste-prevention-plan"
                      />,
                    )}
                    value={values.companyFoodWastePreventionPlan}
                    onChange={(v) =>
                      setFieldValue('companyFoodWastePreventionPlan', v)
                    }
                    isClearable
                    height="150px"
                    error={errors.companyFoodWastePreventionPlan}
                  />
                </div>
                <div className="col-md-12">
                  <AppTextArea
                    icon={<AppRemixIcon icon="font-color" noVariants />}
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-comsaReports"
                        name="company-agreements-third-sector-preventive"
                      />,
                    )}
                    value={values.companyAgreementsThirdSectorPreventive}
                    onChange={(v) =>
                      setFieldValue('companyAgreementsThirdSectorPreventive', v)
                    }
                    isClearable
                    height="150px"
                    error={errors.companyAgreementsThirdSectorPreventive}
                  />
                </div>
                <div className="col-12">
                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator file="module-comsaReports" name="m-cr-comment" />,
                    )}
                    value={values?.comment}
                    onChange={(v) => setFieldValue(`comment`, v)}
                    height="89px"
                  />
                </div>
              </div>

              <div>
                <div className="text-primary mt-3">
                  <Translator file="module-comsaReports" name="attach.evidence" />
                </div>

                <div className="row mb-3">
                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />,
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />,
                            )
                      }
                      onChange={(opt) => setFieldValue('files', opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>

                  <div className="col-sm-12 col-lg-6">
                    <AppFileUploaderList
                      values={values}
                      setFieldValue={setFieldValue}
                      urlsAvaliable={report.files}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button
                    className="btn btn-success btn-block"
                    type="submit"
                    onClick={handleSubmit}
                  >
                    <Translator file="module-comsaReports" name="edit" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
